import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ResearchPage from "@/pages/ResearchPage";
import PublicationsPage from "@/pages/PublicationsPage";
import ImpactPage from "@/pages/ImpactPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import NetworkPage from "@/pages/NetworkPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AccessibilityPage from "@/pages/AccessibilityPage";

// GA4 SPA page-view tracker — fires a page_view on every client-side route change.
// The gtag script itself is loaded from /public/index.html with measurement ID G-K69TXQMNQR.
const GA_MEASUREMENT_ID = "G-K69TXQMNQR";

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("config", GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
}

// Scroll to hash target (e.g. /research#contributions) or top on route change.
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      // Give React a tick to mount the section, then scroll.
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        window.scrollTo({ top: 0, behavior: "instant" });
      }, 60);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location]);
  return null;
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-serif text-slate-900 mb-4">Something went wrong</h1>
            <p className="text-slate-600 mb-4">Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <AnalyticsTracker />
          <ScrollToHash />
          <Navigation />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route
                path="/contributions"
                element={<Navigate to="/publications" replace />}
              />
              <Route path="/publications" element={<PublicationsPage />} />
              <Route
                path="/videos"
                element={<Navigate to="/publications#videos" replace />}
              />
              <Route path="/impact" element={<ImpactPage />} />
              <Route
                path="/global-transformation"
                element={<Navigate to="/impact" replace />}
              />
              <Route path="/opportunities" element={<OpportunitiesPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ErrorBoundary>
  );
}

export default App;
